<template>
    <div class="ml-entity">
        <or-collapsible
            class="ml-entity--collapse-wrapper"
            borderless
            open
        >
            <div slot="header" class="header-text-wrapper">
                <div>
                    {{entityHeader}}
                </div>
                <or-icon-button
                    class="delete-button"
                    icon="delete"
                    size="small"
                    :tooltip="$t('model.nlu-model.sidebar.entities.editors.ml-entity.ml-entity-child.deleteEntityBtnTooltip')"
                    tooltip-position="top center"
                    type="secondary"
                    color="black"
                    @click="deleteEntity"
                    :disabled="isLoading"
                />
            </div>
            <or-textbox
                v-model="$v.localName.$model"
                :label="$t('model.nlu-model.sidebar.entities.editors.ml-entity.ml-entity-child.name.label')"
                :placeholder="$t('model.nlu-model.sidebar.entities.editors.ml-entity.ml-entity-child.name.placeholder')"
                :invalid="!!entityErrors.name"
                :error="entityErrors.name"
            />

            <ml-entities-selector
                :selected-entities.sync="localEntities"
                :entities="$props.entitiesOptions"
                :is-required-selectable="isRequiredFeatureSelectable"
                :is-loading="isLoading"
            />

            <ml-features-selector
                :selected-features.sync="localFeatures"
                :features="allFeatures"
                :is-loading="isLoading || allFeaturesStatus.fetching"
            />

            <ml-entity-child
                v-for="(child, index) in localChildren"
                :key="index"
                :name.sync="child.name"
                :features.sync="child.features"
                :children.sync="child.children"
                :entitiesOptions="$props.entitiesOptions"
                :is-loading="isLoading"
                :taken-names="getTakenNames(index)"
                :invalid="invalid"
                @delete="deleteChild(index)"
            />
            <div v-if="invalid" class="error-message">{{errorMessage}}</div>
            <or-button
                type="secondary"
                color="primary"
                icon="add"
                size="small"
                class="flat create-btn"
                @click="addNewChildEntity"
                :disabled="isLoading || invalid"
                disabledRipple
            >
                {{ localName
                  ? $t('model.nlu-model.sidebar.entities.editors.ml-entity.ml-entity-child.addNewChildEntityButton.description', { entityName: localName })
                  : $t('model.nlu-model.sidebar.entities.editors.ml-entity.ml-entity-child.addNewChildEntityButton.descriptionEmpty') }}
            </or-button>
        </or-collapsible>
    </div>
</template>

<script>
import { MlChildValidationMixin } from '@/mixins/entities/ml-child-validation.mixin'
import { mapState } from 'vuex'
import MlEntitiesSelector from './ml-entities-selection'
import MlFeaturesSelector from './ml-features-selection'

export const getDefaultChild = name => ({
  name,
  features: [],
  children: []
})

export default {
  name: 'ml-entity-child',
  components: { MlFeaturesSelector, MlEntitiesSelector },
  mixins: [MlChildValidationMixin],

  props: {
    name: String,
    // For more info see https://gitlab.com/onereach/platform/nlu/nlu-ui/-/issues/280
    // modelName => Entities;
    // featureName => Phrase;
    features: Array, //  { modelName: string; isRequired: boolean } | { featureName: string; isRequired: boolean }
    children: Array,
    entitiesOptions: Array,
    takenNames: Array,
    isLoading: Boolean,
    invalid: Boolean,
    errorMessage: String
  },

  computed: {
    ...mapState({
      allFeatures: state => state.knowledgebase.features.features,
      allFeaturesStatus: state => state.knowledgebase.features.status
    }),

    entityHeader () {
      return this.$props.name || this.$t('model.nlu-model.sidebar.entities.editors.ml-entity.ml-entity-child.childEntity')
    },

    localName: {
      get () {
        return this.$props.name
      },
      set (value) {
        this.$emit('update:name', value)
      }
    },

    localEntities: {
      get () {
        return this.$props.features.filter(feature => _.has(feature, 'modelName'))
      },
      set (entities) {
        const phrases = this.$props.features.filter(feature => _.has(feature, 'featureName'))
        const features = [...phrases, ...entities]
        this.$emit('update:features', features)
      }
    },

    localFeatures: {
      get () {
        return this.$props.features.filter(feature => _.has(feature, 'featureName'))
      },
      set (phrases) {
        const entities = this.$props.features.filter(feature => _.has(feature, 'modelName'))
        const features = [...phrases, ...entities]
        this.$emit('update:features', features)
      }
    },

    // You can't add required feature if entity has children
    isRequiredFeatureSelectable () {
      return this.localChildren.length === 0
    },

    localChildren: {
      get () {
        return this.$props.children
      },
      set (value) {
        this.$emit('update:children', value)
      }
    }
  },

  methods: {
    addNewChildEntity () {
      this.localChildren = [
        ...this.localChildren,
        getDefaultChild(
          this.$t(
            'model.nlu-model.sidebar.entities.editors.ml-entity.ml-entity-child.childName',
            { childNumber: this.localChildren.length + 1 }
          )
        )
      ]

      const updatedFeatures = this.$props.features.map(feature => ({
        ...feature,
        isRequired: false
      }))
      this.$emit('update:features', updatedFeatures)
    },

    deleteEntity (e) {
      e.stopPropagation()
      this.$emit('delete')
    },

    deleteChild (childPosition) {
      this.localChildren = [...this.localChildren.slice(0, childPosition), ...this.localChildren.slice(childPosition + 1)]
    },

    getTakenNames (idx) {
      const names = []
      this.localChildren.forEach((entity, index) => {
        if (index !== idx) {
          names.push(entity.name)
        }
      })
      return names
    }

  }
}
</script>

<style scoped lang="scss">
    .ml-entity {
        margin-top: 16px;
        ::v-deep {
            .row {
                display: flex;
                justify-content: space-between;
            }
            .feature-selection, .feature-selection--required {
                width: 100%;
                min-width: 50%;
            }
            .feature-selection {
                margin-right: 5px;
            }
        }
        .row {
            display: flex;
            justify-content: space-between;
        }
        .feature-selection, .feature-selection--required {
            width: 100%;
        }
        .feature-selection {
            margin-right: 8px;
        }
        .feature-selection--required {
            margin-left: 8px;
        }
        .error-message {
          color: $or-red;
          padding-left: 20px;
        }
    }
    .ml-entity--collapse-wrapper {
        ::v-deep .header {
            display: flex;
            align-items: center;
        }
        .header-text-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        ::v-deep .header-content {
            width: 100%;
        }
        ::v-deep .body-wrapper > .body {
            padding: 0;
        }
        .delete-button {
            position: relative;
            z-index: 5;
        }
    }
</style>
